import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import AsoebiModal from "@src/components/AsoebiModal";
import Dialog from "src/components/Dialog";

interface Props {
  eventUuid: string | undefined;
}

const AsoEbi = ({ eventUuid }: Props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const borderVariants = {
    hidden: {
      opacity: 0,
      scaleX: 0,
    },
    visible: {
      opacity: 1,
      scaleX: 1,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "backOut",
        delay: 1.2,
      },
    },
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
      },
    },
  };

  const [openAsoebi, setOpenAsoebi] = useState(false);

  return (
    <motion.div
      className="min-h-[50vh] bg-[rgba(236,228,237,1)] flex flex-col justify-center py-[4rem] md:py-[5rem] md:pt-[6rem]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div
        ref={ref}
        className="flex flex-col justify-center items-center relative px-4 sm:px-8 py-[4rem] bg-[rgba(236,228,237,1)]"
      >
        {/* Top border */}
        <motion.div
          className="flex justify-center h-5 w-full sm:w-[90%] relative px-8 overflow-hidden mb-10"
          style={{
            backgroundImage: `url('/images/Frame (3).svg')`,
            backgroundSize: "cover",
            backgroundRepeat: "repeat-x",
            backgroundPosition: "top left",
          }}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={borderVariants}
        />

        {/* Main content */}
        <motion.div
          className="flex flex-col gap-y-8 sm:flex-row justify-between items-center w-full lg:w-[90%] relative z-10 my-4 lg:my-10 lg:mx-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h3
            className="font-silver font-light text-2xl sm:text-4xl text-center text-black lg:w-[35%]"
            variants={itemVariants}
            whileHover={{
              scale: 1.02,
              color: "#8B7A85",
              transition: { duration: 0.3 },
            }}
          >
            Check out the wedding asoebi for our big day!
          </motion.h3>

          <Dialog
            trigger={
              <motion.div
                className="flex flex-col md:flex-row gap-8 justify-center items-center"
                variants={itemVariants}
              >
                <motion.button
                  className="px-8 py-3 rounded transition duration-300 border-black text-black text-xl font-sans uppercase hover:bg-black hover:text-white relative overflow-hidden"
                  type="button"
                  variants={buttonVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover="hover"
                  whileTap="tap"
                >
                  {/* Animated border glow */}
                  <motion.div
                    className="absolute inset-0 border-2 border-black rounded"
                    animate={{
                      boxShadow: [
                        "0 0 0px rgba(96, 83, 90, 0.3)",
                        "0 0 15px rgba(96, 83, 90, 0.6)",
                        "0 0 0px rgba(96, 83, 90, 0.3)",
                      ],
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                  />
                  <p className="relative z-10">See Asoebi</p>
                </motion.button>
              </motion.div>
            }
            open={openAsoebi}
            onOpenChange={setOpenAsoebi}
          >
            <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
          </Dialog>
        </motion.div>

        {/* Bottom border */}
        <motion.div
          className="flex justify-center h-5 w-full sm:w-[90%] relative px-8 overflow-hidden mt-10"
          style={{
            backgroundImage: `url('/images/Frame (3).svg')`,
            backgroundSize: "cover",
            backgroundRepeat: "repeat-x",
            backgroundPosition: "top left",
          }}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={borderVariants}
        />
      </div>
    </motion.div>
  );
};

export default AsoEbi;
